import { useCallback, useEffect, useRef, useState } from 'react';
import type { LoyaltyAccountSummary, LoyaltyEligibleCustomer, LoyaltyLedgerHistoryItem, LoyaltyTier } from '@raptor/contracts';
import { ApiResponseError, apiClient } from '../lib/api-client.js';

function newIdempotencyKey(): string {
  return `loyalty-${crypto.randomUUID()}`;
}

export function LoyaltyWorkspace({
  organizationId,
  branchId,
  canManageTiers,
}: {
  organizationId: string;
  branchId: string;
  canManageTiers: boolean;
}) {
  const [customers, setCustomers] = useState<LoyaltyEligibleCustomer[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [account, setAccount] = useState<LoyaltyAccountSummary | null>(null);
  const [history, setHistory] = useState<LoyaltyLedgerHistoryItem[]>([]);
  const [tiers, setTiers] = useState<LoyaltyTier[]>([]);
  const [tierName, setTierName] = useState('');
  const [minimumPoints, setMinimumPoints] = useState('');
  const [tierId, setTierId] = useState('');
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const workspaceGeneration = useRef(0);
  const customerLoadGeneration = useRef(0);
  const accountLoadGeneration = useRef(0);
  const tierLoadGeneration = useRef(0);

  const loadCustomers = useCallback(async () => {
    const workspace = workspaceGeneration.current;
    const generation = ++customerLoadGeneration.current;
    setError(null);
    try {
      const nextCustomers = await apiClient.getLoyaltyCustomers(organizationId);
      if (workspace !== workspaceGeneration.current || generation !== customerLoadGeneration.current) return;
      setCustomers(nextCustomers);
      setCustomerId((current) => nextCustomers.some((customer) => customer.id === current)
        ? current
        : nextCustomers[0]?.id ?? '');
    } catch {
      if (workspace !== workspaceGeneration.current || generation !== customerLoadGeneration.current) return;
      setCustomers([]);
      setCustomerId('');
      setError('Loyalty customers are temporarily unavailable.');
    }
  }, [organizationId]);

  useEffect(() => {
    setCustomerId((current) => customers.some((customer) => customer.id === current) ? current : customers[0]?.id ?? '');
  }, [customers]);

  useEffect(() => {
    ++workspaceGeneration.current;
    ++customerLoadGeneration.current;
    ++accountLoadGeneration.current;
    ++tierLoadGeneration.current;
    setCustomers([]);
    setCustomerId('');
    setAccount(null);
    setHistory([]);
    setTiers([]);
    setTierId('');
    void loadCustomers();
  }, [loadCustomers]);

  const load = useCallback(async () => {
    const workspace = workspaceGeneration.current;
    const generation = ++accountLoadGeneration.current;
    if (!customerId) { setAccount(null); setHistory([]); return; }
    setError(null);
    try {
      const [nextAccount, nextHistory] = await Promise.all([
        apiClient.getLoyaltyAccount(customerId), apiClient.getLoyaltyHistory(customerId),
      ]);
      if (workspace !== workspaceGeneration.current || generation !== accountLoadGeneration.current) return;
      setAccount(nextAccount);
      setHistory(nextHistory);
    } catch (cause) {
      if (workspace !== workspaceGeneration.current || generation !== accountLoadGeneration.current) return;
      // A customer has no loyalty account until their first governed command.
      if (cause instanceof ApiResponseError && cause.status === 404) { setAccount(null); setHistory([]); return; }
      setError('Loyalty data is temporarily unavailable.');
    }
  }, [customerId]);

  useEffect(() => { void load(); }, [load]);

  const loadTiers = useCallback(async () => {
    const workspace = workspaceGeneration.current;
    const generation = ++tierLoadGeneration.current;
    if (!canManageTiers) { setTiers([]); setTierId(''); return; }
    try {
      const nextTiers = await apiClient.getLoyaltyTiers(organizationId);
      if (workspace !== workspaceGeneration.current || generation !== tierLoadGeneration.current) return;
      setTiers(nextTiers);
      setTierId((current) => nextTiers.some((tier) => tier.id === current) ? current : nextTiers[0]?.id ?? '');
    } catch {
      if (workspace !== workspaceGeneration.current || generation !== tierLoadGeneration.current) return;
      setTiers([]);
      setTierId('');
      setError('Loyalty tiers are temporarily unavailable.');
    }
  }, [canManageTiers, organizationId]);

  useEffect(() => { void loadTiers(); }, [loadTiers]);

  const apply = async (kind: 'earn' | 'redeem') => {
    const amount = Number(points);
    if (!Number.isInteger(amount) || amount < 1) { setError('Enter a whole number of points.'); return; }
    if (!customerId) { setError('Select a customer first.'); return; }
    setBusy(true); setError(null);
    try {
      await apiClient.applyLoyaltyPoints(kind, {
        organizationId, branchId, customerId, points: amount, idempotencyKey, note: note.trim() || undefined,
      });
      setPoints(''); setNote(''); setIdempotencyKey(newIdempotencyKey());
      await load();
    } catch (cause) {
      setError(cause instanceof ApiResponseError && cause.status === 409
        ? 'This loyalty command has already been used with different details.'
        : kind === 'redeem' ? 'Points could not be redeemed. Check the available balance and try again.' : 'Points could not be earned.');
    } finally { setBusy(false); }
  };

  const createTier = async () => {
    const threshold = Number(minimumPoints);
    if (!tierName.trim() || !Number.isInteger(threshold) || threshold < 0) {
      setError('Enter a tier name and a whole-number minimum points threshold.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await apiClient.createLoyaltyTier({ organizationId, name: tierName.trim(), minimumPoints: threshold });
      setTierName(''); setMinimumPoints('');
      await loadTiers();
    } catch {
      setError('Loyalty tier could not be created.');
    } finally { setBusy(false); }
  };

  const assignTier = async () => {
    if (!customerId || !tierId) { setError('Select a customer and tier first.'); return; }
    setBusy(true); setError(null);
    try {
      await apiClient.assignLoyaltyTier(customerId, tierId);
      await load();
    } catch {
      setError('Loyalty tier could not be assigned.');
    } finally { setBusy(false); }
  };

  return <section className="workspace-rail" aria-labelledby="loyalty-title">
    <div className="rail-heading"><div><p className="eyebrow">Governed loyalty</p><h2 id="loyalty-title">Customer points</h2></div><button className="text-action" type="button" onClick={() => { void loadCustomers(); void load(); void loadTiers(); }}>Refresh</button></div>
    {!customers.length ? <><p>No customers are available for loyalty yet.</p>{error ? <p role="alert">{error}</p> : null}</> : <>
      <label>Customer<select aria-label="Loyalty customer" value={customerId} onChange={(event) => setCustomerId(event.target.value)}>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.displayName}</option>)}</select></label>
      <div className="context-card"><p className="eyebrow">Derived balance</p><strong>{account?.balancePoints ?? '0'} points</strong><span className="context-card__meta">Loyalty tier: {account?.tierName ?? 'Not assigned'} · calculated from an append-only ledger</span></div>
      {canManageTiers ? <section aria-labelledby="tier-management-title">
        <h3 id="tier-management-title">Tier management</h3>
        <form className="crm-form" onSubmit={(event) => { event.preventDefault(); void createTier(); }}>
          <label>Tier name<input required maxLength={80} value={tierName} onChange={(event) => setTierName(event.target.value)} /></label>
          <label>Minimum points<input aria-label="Minimum points" required inputMode="numeric" min="0" step="1" value={minimumPoints} onChange={(event) => setMinimumPoints(event.target.value)} /></label>
          <button className="secondary-action" disabled={busy} type="submit">Create tier</button>
        </form>
        <p className="context-card__meta">Minimum points is an administrator-defined threshold only. Tier assignment is manual; earning points does not automatically promote a customer.</p>
        {tiers.length ? <form className="crm-form" onSubmit={(event) => { event.preventDefault(); void assignTier(); }}>
          <label>Assign loyalty tier<select aria-label="Assign loyalty tier" value={tierId} onChange={(event) => setTierId(event.target.value)}>{tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name} — minimum {tier.minimumPoints} points</option>)}</select></label>
          <button className="secondary-action" disabled={busy} type="submit">Assign tier</button>
        </form> : <p>No loyalty tiers have been created.</p>}
      </section> : null}
      <form className="crm-form" onSubmit={(event) => { event.preventDefault(); void apply('earn'); }}>
        <label>Points<input aria-label="Loyalty points" required inputMode="numeric" min="1" step="1" value={points} onChange={(event) => setPoints(event.target.value)} /></label>
        <label>Note (optional)<input maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        <button className="secondary-action" disabled={busy} type="submit">Earn points</button>
        <button className="text-action" disabled={busy} type="button" onClick={() => void apply('redeem')}>Redeem points</button>
      </form>
      {error ? <p role="alert">{error}</p> : null}
      <h3>Ledger history</h3>
      {history.length ? <ol className="crm-list">{history.map((entry) => <li key={entry.ledgerEntryId}><strong>{entry.entryKind === 'EARN' ? '+' : ''}{entry.pointsDelta} points</strong><span>Balance after: {entry.balanceAfter} · {new Date(entry.occurredAt).toLocaleString()}</span>{entry.note ? <small>{entry.note}</small> : null}</li>)}</ol> : <p>No loyalty activity for this customer.</p>}
    </>}
  </section>;
}
